import { useState } from 'react';
import { useCustom, useNavigation } from '@refinedev/core';
import { Row, Col, Card, Statistic, Typography, Spin, List, Tag, Avatar, Button, Segmented } from 'antd';
import {
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ShopOutlined,
  SettingOutlined,
  DollarOutlined,
  FileProtectOutlined,
  UserAddOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  AimOutlined,
  WalletOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { isBusOnlyMode, isTaxiOnlyMode } from '../contexts/ServiceContext';
import { t } from '../i18n';

type DashboardRange = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'allTime';

const { Title, Text } = Typography;

interface TaxiDashboardStats {
  // Original nested structure (kept for compatibility)
  users: {
    total: number;
    drivers: number;
    verifiedDrivers: number;
    onlineDrivers: number;
  };
  trips: {
    today: number;
    thisMonth: number;
    completedToday: number;
    cancelledToday: number;
    monthlyGrowth: number;
  };
  operations: {
    pendingVerifications: number;
    openTickets: number;
  };
  revenue: {
    today: number;
    thisMonth: number;
  };
  // Flat KPI fields
  onlineDrivers: number;
  activeTrips: number;
  commissionDeducted: number;
  driverTopups: number;
  adminTopups: number;
  pendingDocuments: number;
  openTickets: number;
  completedOrders: number;
  completedOrdersByType: {
    interregion: number;
    shaharlarAro: number;
    yolYolakay: number;
    postage: number;
  };
  newUsersToday: number;
  // Growth percentages
  onlineDriversGrowth: number | null;
  activeTripsGrowth: number | null;
  commissionDeductedGrowth: number | null;
  driverTopupsGrowth: number | null;
  adminTopupsGrowth: number | null;
  pendingDocumentsGrowth: number | null;
  openTicketsGrowth: number | null;
  completedOrdersGrowth: number | null;
  newUsersTodayGrowth: number | null;
  // Trips by type
  tripsByType: {
    INTERREGION_TAXI: number;
    SHAHARLAR_ARO: number;
    YOL_YOLAKAY: number;
    POSTAGE: number;
  };
}

interface BusDashboardStats {
  companies: {
    total: number;
    active: number;
  };
  trips: {
    total: number;
    scheduled: number;
    today: number;
  };
  bookings: {
    total: number;
    today: number;
  };
  revenue: {
    total: number;
    today: number;
  };
}

interface AuditLog {
  id: number;
  action: string;
  entity: string;
  entityId: number | null;
  createdAt: string;
  staff: {
    id: number;
    username: string;
    fullName: string | null;
  };
}

// Maps audit-log `action` keys (recorded by the backend) to human-readable
// labels in the active locale. We resolve via a function rather than a static
// map so labels reflect the current locale even if the user toggles it
// without a full reload.
const getActionLabel = (action: string): string => {
  const labels: Record<string, string> = {
    'bus.company.create': t('audit.companyCreated'),
    'bus.company.update': t('audit.companyUpdated'),
    'bus.trip.create': t('audit.tripCreated'),
    'bus.trip.update': t('audit.tripUpdated'),
    'bus.trip.cancel': t('audit.tripCancelled'),
    'bus.booking.create': t('audit.bookingCreated'),
    'bus.booking.confirm': t('audit.bookingConfirmed'),
    'bus.booking.cancel': t('audit.bookingCancelled'),
    'bus.station.create': t('audit.stationCreated'),
    'bus.layout.create': t('audit.layoutCreated'),
    'staff.create': t('audit.staffCreated'),
    'staff.update': t('audit.staffUpdated'),
    'staff.delete': t('audit.staffDeleted'),
    'ticket.reply': t('audit.ticketReplied'),
    'ticket.resolve': t('audit.ticketResolved'),
    'faq.create': t('audit.faqCreated'),
    'faq.update': t('audit.faqUpdated'),
    'promo.create': t('audit.promoCreated'),
    'settings.update': t('audit.settingsUpdated'),
    'wallet.topup': t('audit.walletTopup'),
    'wallet.deduct': t('audit.walletDeduct'),
  };
  return labels[action] || action;
};

const actionColors: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  confirm: 'green',
  cancel: 'orange',
  reply: 'blue',
  resolve: 'green',
};

const GrowthIndicator = ({ growth }: { growth: number | null | undefined }) => {
  if (growth === null || growth === undefined) return null;

  const isPositive = growth >= 0;
  const color = isPositive ? '#52c41a' : '#ff4d4f';
  const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;

  return (
    <Text style={{ fontSize: 12, color }}>
      <Icon style={{ fontSize: 10, marginRight: 2 }} />
      {Math.abs(growth)}% {t('dashboard.comparedYesterday')}
    </Text>
  );
};

export const Dashboard = () => {
  const { push } = useNavigation();
  const busOnlyMode = isBusOnlyMode();
  const taxiOnlyMode = isTaxiOnlyMode();
  const [range, setRange] = useState<DashboardRange>('today');

  // Bus stats fetch
  const { data: busData, isLoading: busLoading } = useCustom<BusDashboardStats>({
    url: '/admin/bus/stats',
    method: 'get',
    queryOptions: {
      enabled: busOnlyMode,
    },
  });

  // Taxi dashboard stats fetch — refine derives the query cache key from
  // `config.query`, so changing `range` automatically re-fetches the stats.
  const { data: taxiData, isLoading: taxiLoading } = useCustom<TaxiDashboardStats>({
    url: '/admin/dashboard',
    method: 'get',
    config: {
      query: { range },
    },
    queryOptions: {
      enabled: !busOnlyMode,
      refetchInterval: 60000, // Refresh every 60 seconds
    },
  });

  // Audit log fetch
  const { data: activityData, isLoading: activityLoading } = useCustom<{ data: AuditLog[] }>({
    url: '/admin/audit-logs',
    method: 'get',
    config: {
      query: {
        limit: 10,
      },
    },
  });

  const busStats = busData?.data;
  const taxiStats = taxiData?.data;
  const recentActivity = activityData?.data?.data || [];

  if ((busOnlyMode && busLoading) || (!busOnlyMode && taxiLoading)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
  };

  const getActionColor = (action: string) => {
    const parts = action.split('.');
    const actionType = parts[parts.length - 1];
    return actionColors[actionType] || 'default';
  };

  const quickActions = busOnlyMode
    ? [
        {
          title: t('nav.busTrips'),
          icon: <CalendarOutlined />,
          onClick: () => push('/bus/trips/create'),
          color: '#9B59B6',
        },
        {
          title: t('nav.busCompanies'),
          icon: <ShopOutlined />,
          onClick: () => push('/bus/companies'),
          color: '#3498DB',
        },
        {
          title: t('nav.bookings'),
          icon: <CheckCircleOutlined />,
          onClick: () => push('/bus/bookings'),
          color: '#27AE60',
        },
        {
          title: t('nav.settings'),
          icon: <SettingOutlined />,
          onClick: () => push('/bus/settings'),
          color: '#7F8C8D',
        },
      ]
    : [
        {
          title: t('nav.drivers'),
          icon: <CarOutlined />,
          onClick: () => push('/drivers'),
          color: '#1ABC9C',
        },
        {
          title: t('nav.documents'),
          icon: <CheckCircleOutlined />,
          onClick: () => push('/documents'),
          color: '#E67E22',
        },
        {
          title: t('nav.trips'),
          icon: <EnvironmentOutlined />,
          onClick: () => push('/trips'),
          color: '#3498DB',
        },
        {
          title: t('nav.users'),
          icon: <UserOutlined />,
          onClick: () => push('/users'),
          color: '#9B59B6',
        },
      ];

  // KPI stat card definitions for taxi mode. The financial cards now show
  // commission deducted (renamed from "today revenue") plus separate cards
  // for driver-initiated and admin-initiated balance topups so the office
  // can see how much liquidity each side is contributing.
  type KpiCard = {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    growth: number | null | undefined;
    formatter?: (val: number) => string;
    breakdown?: { label: string; value: number; color: string }[];
  };

  const taxiKpiCards: KpiCard[] = [
    {
      title: t('dashboard.onlineDrivers'),
      value: taxiStats?.onlineDrivers ?? 0,
      icon: <CarOutlined />,
      color: '#52c41a',
      growth: taxiStats?.onlineDriversGrowth,
    },
    {
      title: t('dashboard.activeTrips'),
      value: taxiStats?.activeTrips ?? 0,
      icon: <EnvironmentOutlined />,
      color: '#1890ff',
      growth: taxiStats?.activeTripsGrowth,
    },
    {
      title: t('dashboard.commissionDeducted'),
      value: taxiStats?.commissionDeducted ?? 0,
      icon: <DollarOutlined />,
      color: '#1ABC9C',
      growth: taxiStats?.commissionDeductedGrowth,
      formatter: (val: number) => formatCurrency(val),
    },
    {
      title: t('dashboard.driverTopups'),
      value: taxiStats?.driverTopups ?? 0,
      icon: <WalletOutlined />,
      color: '#27AE60',
      growth: taxiStats?.driverTopupsGrowth,
      formatter: (val: number) => formatCurrency(val),
    },
    {
      title: t('dashboard.adminTopups'),
      value: taxiStats?.adminTopups ?? 0,
      icon: <BankOutlined />,
      color: '#9B59B6',
      growth: taxiStats?.adminTopupsGrowth,
      formatter: (val: number) => formatCurrency(val),
    },
    {
      title: t('dashboard.pendingDocs'),
      value: taxiStats?.pendingDocuments ?? 0,
      icon: <FileProtectOutlined />,
      color: '#fa8c16',
      growth: taxiStats?.pendingDocumentsGrowth,
    },
    {
      title: t('dashboard.completedOrders'),
      value: taxiStats?.completedOrders ?? 0,
      icon: <CheckCircleOutlined />,
      color: '#13C2C2',
      growth: taxiStats?.completedOrdersGrowth,
      breakdown: [
        {
          label: t('dashboard.completedOrders.interregion'),
          value: taxiStats?.completedOrdersByType?.interregion ?? 0,
          color: 'blue',
        },
        {
          label: t('dashboard.completedOrders.shaharlarAro'),
          value: taxiStats?.completedOrdersByType?.shaharlarAro ?? 0,
          color: 'green',
        },
        {
          label: t('dashboard.completedOrders.yolYolakay'),
          value: taxiStats?.completedOrdersByType?.yolYolakay ?? 0,
          color: 'orange',
        },
        {
          label: t('dashboard.completedOrders.postage'),
          value: taxiStats?.completedOrdersByType?.postage ?? 0,
          color: 'purple',
        },
      ],
    },
    {
      title: t('dashboard.newUsers'),
      value: taxiStats?.newUsersToday ?? 0,
      icon: <UserAddOutlined />,
      color: '#722ed1',
      growth: taxiStats?.newUsersTodayGrowth,
    },
  ];

  // Active trip type labels
  const tripTypeLabels: Record<string, { label: string; color: string }> = {
    INTERREGION_TAXI: { label: t('tripType.interregionTaxi'), color: 'blue' },
    SHAHARLAR_ARO: { label: t('tripType.shaharlarAro'), color: 'green' },
    YOL_YOLAKAY: { label: t('tripType.yolYolakay'), color: 'orange' },
    POSTAGE: { label: t('tripType.postage'), color: 'purple' },
  };

  const rangeOptions: { label: string; value: DashboardRange }[] = [
    { label: t('dashboard.range.today'), value: 'today' },
    { label: t('dashboard.range.yesterday'), value: 'yesterday' },
    { label: t('dashboard.range.thisWeek'), value: 'thisWeek' },
    { label: t('dashboard.range.thisMonth'), value: 'thisMonth' },
    { label: t('dashboard.range.thisYear'), value: 'thisYear' },
    { label: t('dashboard.range.allTime'), value: 'allTime' },
  ];

  return (
    <div className="p-6">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          {busOnlyMode
            ? t('dashboard.busManagement')
            : taxiOnlyMode
              ? t('dashboard.taxiManagement')
              : t('dashboard.title')}
        </Title>
        {!busOnlyMode && (
          <Segmented<DashboardRange>
            value={range}
            onChange={(val) => setRange(val as DashboardRange)}
            options={rangeOptions}
          />
        )}
      </div>

      {/* Taxi KPI Stats Cards */}
      {!busOnlyMode && (
        <>
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            {t('dashboard.keyMetrics')}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {taxiKpiCards.map((kpi, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card styles={{ body: { padding: '20px 24px' } }}>
                  <Statistic
                    title={kpi.title}
                    value={kpi.value}
                    prefix={
                      <span style={{ color: kpi.color, fontSize: 20 }}>
                        {kpi.icon}
                      </span>
                    }
                    valueStyle={{ color: kpi.color, fontSize: 28 }}
                    formatter={
                      kpi.formatter
                        ? (value) => kpi.formatter!(Number(value))
                        : undefined
                    }
                  />
                  <div style={{ marginTop: 4 }}>
                    <GrowthIndicator growth={kpi.growth} />
                  </div>
                  {kpi.breakdown && (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 4,
                      }}
                    >
                      {kpi.breakdown.map((b) => (
                        <Tag key={b.label} color={b.color} style={{ margin: 0 }}>
                          {b.label}: {b.value}
                        </Tag>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>

          {/* Active Now Section */}
          {taxiStats?.tripsByType && (
            <div
              style={{
                marginBottom: 24,
                padding: '12px 16px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <Text strong style={{ fontSize: 13, color: '#7F8C8D', marginRight: 4 }}>
                {t('dashboard.activeNow')}:
              </Text>
              {Object.entries(taxiStats.tripsByType).map(([type, count]) => {
                const info = tripTypeLabels[type];
                if (!info) return null;
                return (
                  <Tag key={type} color={info.color} style={{ margin: 0 }}>
                    {info.label}: {count}
                  </Tag>
                );
              })}
              <Button
                type="link"
                size="small"
                icon={<AimOutlined />}
                onClick={() => push('/live-map')}
                style={{ marginLeft: 'auto', padding: 0 }}
              >
                {t('dashboard.viewOnMap')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Quick Actions */}
      <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
        {t('dashboard.quickActions')}
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {quickActions.map((action, index) => (
          <Col xs={12} sm={8} md={6} key={index}>
            <Card
              hoverable
              onClick={action.onClick}
              style={{ textAlign: 'center', cursor: 'pointer' }}
              styles={{ body: { padding: 16 } }}
            >
              <div
                style={{
                  fontSize: 24,
                  color: action.color,
                  marginBottom: 8,
                }}
              >
                {action.icon}
              </div>
              <Text style={{ fontSize: 12 }}>{action.title}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      {busOnlyMode ? (
        <>
          {/* Bus Companies Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            {t('dashboard.companies')}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('dashboard.totalCompanies')}
                  value={busStats?.companies?.total || 0}
                  prefix={<ShopOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('dashboard.activeCompanies')}
                  value={busStats?.companies?.active || 0}
                  prefix={<CheckCircleOutlined style={{ color: '#27AE60' }} />}
                  valueStyle={{ color: '#27AE60' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Bus Trips Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            {t('nav.busTrips')}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title={t('dashboard.totalTrips')}
                  value={busStats?.trips?.total || 0}
                  prefix={<CalendarOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title={t('dashboard.todayTrips')}
                  value={busStats?.trips?.today || 0}
                  prefix={<CalendarOutlined style={{ color: '#3498DB' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title={t('dashboard.scheduledTrips')}
                  value={busStats?.trips?.scheduled || 0}
                  prefix={<ClockCircleOutlined style={{ color: '#F39C12' }} />}
                  valueStyle={{ color: '#F39C12' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Bus Bookings Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            {t('dashboard.bookings')}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title={t('dashboard.totalBookings')}
                  value={busStats?.bookings?.total || 0}
                  prefix={<UserOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title={t('dashboard.todayBookings')}
                  value={busStats?.bookings?.today || 0}
                  prefix={<UserOutlined style={{ color: '#3498DB' }} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Revenue Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            {t('dashboard.revenue')}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title={t('dashboard.totalRevenue')}
                  value={busStats?.revenue?.total || 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  valueStyle={{ color: '#9B59B6' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title={t('dashboard.todayRevenue')}
                  value={busStats?.revenue?.today || 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  valueStyle={{ color: '#3498DB' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      {/* Recent Activity */}
      <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
        {t('dashboard.recentActivity')}
      </Title>
      <Card styles={{ body: { padding: 0 } }}>
        <List
          loading={activityLoading}
          dataSource={recentActivity}
          size="small"
          renderItem={(item: AuditLog) => (
            <List.Item style={{ padding: '12px 16px' }}>
              <List.Item.Meta
                avatar={
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#9B59B6' }}
                  />
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {item.staff?.fullName || item.staff?.username}
                    </Text>
                    <Tag color={getActionColor(item.action)} style={{ margin: 0 }}>
                      {getActionLabel(item.action)}
                    </Tag>
                  </div>
                }
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(item.createdAt).toLocaleString('uz-UZ')}
                  </Text>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: t('dashboard.noActivity') }}
        />
      </Card>
    </div>
  );
};
